import { CommandDispatcher } from "./CommandDispatcher.js";
import { emitCommandEvent } from "./commandEvents.js";
import type { MenuEntry, TopLevelMenu } from "./menuTypes.js";
import { formatMenuShortcutForUi } from "./shortcutDisplay.js";

export type MenuBarOptions = {
  dispatcher: CommandDispatcher;
  /** Fires after dispatcher runs (for shell integration). */
  eventTarget?: EventTarget;
};

/**
 * In-page menubar (`role="menubar"`) per WEB_IMPLEMENTATION_LAYER.md §24.
 */
export class MenuBar {
  private readonly root: HTMLElement;
  private readonly dispatcher: CommandDispatcher;
  private readonly eventTarget: EventTarget;
  private openDropdown: HTMLElement | null = null;

  constructor(model: readonly TopLevelMenu[], options: MenuBarOptions) {
    this.dispatcher = options.dispatcher;
    this.eventTarget = options.eventTarget ?? document.body;
    this.root = document.createElement("nav");
    this.root.className = "npp-menubar";
    this.root.setAttribute("role", "menubar");
    this.root.setAttribute("aria-label", "Notepad++");
    for (const top of model) this.root.appendChild(this.createTopMenu(top));
    document.addEventListener("click", this.onDocClick, true);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  destroy(): void {
    document.removeEventListener("click", this.onDocClick, true);
    this.root.remove();
  }

  /** Replace the menu tree (e.g. MRU list changed). Preserves dispatcher / listeners. */
  setModel(model: readonly TopLevelMenu[]): void {
    this.closeDropdown();
    this.root.replaceChildren();
    for (const top of model) this.root.appendChild(this.createTopMenu(top));
  }

  get element(): HTMLElement {
    return this.root;
  }

  private onDocClick = (ev: MouseEvent) => {
    if (!this.openDropdown) return;
    const t = ev.target as Node;
    if (this.root.contains(t)) return;
    this.closeDropdown();
  };

  private closeDropdown(): void {
    if (!this.openDropdown) return;
    this.openDropdown.dataset.open = "false";
    const btn = this.openDropdown.previousElementSibling as HTMLButtonElement | null;
    btn?.setAttribute("aria-expanded", "false");
    this.openDropdown = null;
  }

  private toggleDropdown(drop: HTMLElement, btn: HTMLButtonElement): void {
    const willOpen = drop.dataset.open !== "true";
    this.closeDropdown();
    if (willOpen) {
      drop.dataset.open = "true";
      btn.setAttribute("aria-expanded", "true");
      this.openDropdown = drop;
    }
  }

  private createTopMenu(top: TopLevelMenu): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "npp-menu-top";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npp-menu-top-button";
    btn.textContent = top.label;
    btn.setAttribute("aria-haspopup", "true");
    btn.setAttribute("aria-expanded", "false");
    btn.setAttribute("role", "menuitem");

    const drop = document.createElement("div");
    drop.className = "npp-menu-dropdown";
    drop.dataset.open = "false";
    drop.setAttribute("role", "menu");

    for (const entry of top.entries) {
      const node = this.renderEntry(entry);
      if (node) drop.appendChild(node);
    }

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleDropdown(drop, btn);
    });

    wrap.append(btn, drop);
    return wrap;
  }

  private renderEntry(entry: MenuEntry): HTMLElement | null {
    if (entry.type === "separator") {
      const hr = document.createElement("hr");
      hr.className = "npp-menu-separator";
      return hr;
    }
    if (entry.type === "submenu") {
      const box = document.createElement("div");
      box.className = "npp-menu-submenu";
      const lab = document.createElement("div");
      lab.className = "npp-menu-submenu-label";
      lab.textContent = entry.label;
      box.appendChild(lab);
      for (const ch of entry.children) {
        const n = this.renderEntry(ch);
        if (n) box.appendChild(n);
      }
      return box;
    }
    const item = document.createElement("button");
    item.type = "button";
    item.className = "npp-menuitem";
    const labelSpan = document.createElement("span");
    labelSpan.textContent = entry.label;
    item.appendChild(labelSpan);
    item.dataset.commandId = entry.id;
    const enabled = entry.enabled !== false;
    item.disabled = !enabled;
    if (entry.checkbox) {
      item.setAttribute("role", "menuitemcheckbox");
      item.setAttribute("aria-checked", entry.checked ? "true" : "false");
    } else {
      item.setAttribute("role", "menuitem");
    }
    if (entry.shortcut) {
      const sc = document.createElement("span");
      sc.className = "npp-shortcut";
      sc.setAttribute("aria-label", entry.shortcut);
      sc.textContent = formatMenuShortcutForUi(entry.shortcut);
      item.appendChild(sc);
    }
    item.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!this.dispatcher.isCommandEnabled(entry.id)) return;
      this.dispatcher.executeCommand(entry.id);
      emitCommandEvent(this.eventTarget, { commandId: entry.id, source: "menu" });
      this.closeDropdown();
    });
    return item;
  }

  /** Sync checkbox state from dispatcher (e.g. after View → Word wrap toggled). */
  syncCheckboxStates(commandIds: readonly string[]): void {
    for (const id of commandIds) {
      const el = this.root.querySelector<HTMLElement>(`[data-command-id="${id}"]`);
      if (!el || el.getAttribute("role") !== "menuitemcheckbox") continue;
      el.setAttribute(
        "aria-checked",
        this.dispatcher.isCommandChecked(id) ? "true" : "false",
      );
    }
  }
}
